import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import Layout from "./Layout";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ExternalLink, Github, Menu } from "lucide-react";

export default function PortfolioBackend() {
  const { isDark } = useTheme();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const projects = [
    {
      title: "Nigerian Tax Calculator API (Present)",
      description: `Building a comprehensive tax calculation system for Nigerian Personal Income Tax (PIT) and Limited Liability Company (LLC) tax regulations. The backend implements Clean Architecture with domain-driven design, featuring PDF tax document parsing, fuzzy matching for tax bracket identification, and secure PIN encryption. Designed to help individuals and businesses determine tax liability and maintain compliance with Nigerian tax laws.`,
      areasOfFocus: [
        "Clean Architecture with DDD principles",
        "REST API development with Actix-web",
        "PostgreSQL database with SQLx",
        "PDF parsing and text extraction",
        "Fuzzy string matching algorithms",
        "Cryptographic security for sensitive data",
      ],
      techStack: [
        "Rust",
        "Actix-web",
        "PostgreSQL",
        "SQLx",
        "PDF-Extract",
        "Ring Crypto",
      ],
      githubUrl: "https://github.com/Mazikiii/Nigeria-tax-calculator-backend",
      liveUrl: null,
    },
    {
      title: "Vendor Management System (2024)",
      description: `Developed a full-stack vendor and product management system for tracking company purchases and supplier relationships. Implemented comprehensive CRUD operations with RESTful API endpoints, database migrations, and user authentication. The system manages products, vendors, purchases, brands, categories, units, and addresses with relational data integrity.`,
      areasOfFocus: [
        "MVC architecture pattern",
        "RESTful API design (GET, POST, PUT, PATCH, DELETE)",
        "Database migrations and relationships",
        "User authentication with Laravel Sanctum",
        "Frontend integration with Blade templates",
        "CRUD operations and data validation",
      ],
      techStack: ["PHP", "Laravel 8", "MySQL", "JavaScript", "CSS", "Blade"],
      githubUrl: "https://github.com/Mazikiii/Vendor_Management_System",
      liveUrl: null,
    },
    {
      title: "Drink Inventory Management System (2024)",
      description: `Built a Spring Boot application for managing beverage inventory with MySQL database integration. Implemented full MVC architecture with JPA/Hibernate ORM for entity management, featuring drink catalog with ingredients, pricing, and company information. Includes automated timestamp tracking and one-to-many relationships for complex data modeling.`,
      areasOfFocus: [
        "Spring Boot MVC architecture",
        "JPA/Hibernate ORM implementation",
        "Entity relationship mapping",
        "RESTful endpoints with Thymeleaf views",
        "MySQL database integration via XAMPP",
        "Automated audit timestamps",
      ],
      techStack: [
        "Java 17",
        "Spring Boot 3",
        "Spring Data JPA",
        "MySQL",
        "Thymeleaf",
        "Maven",
      ],
      githubUrl: "https://github.com/Mazikiii/SpringToXampp",
      liveUrl: null,
    },
  ];

  return (
    <Layout>
      {({ toggleMobileMenu }) => (
        <div className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-12">
          <div className="w-full max-w-content">
            {/* Mobile Menu Button */}
            <div className="mb-8 lg:hidden">
              <button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-lg ${
                  isDark
                    ? "text-gray-400 hover:text-white hover:bg-gray-700"
                    : "text-text-muted hover:text-text hover:bg-gray-100"
                }`}
                aria-label="Toggle menu"
              >
                <Menu size={20} />
              </button>
            </div>

            {/* Page Header */}
            <header className="mb-12">
              {/* Breadcrumb Navigation */}
              <nav className="mb-6">
                <ol className="flex items-center gap-2 text-sm list-none">
                  <li>
                    <Link
                      to="/portfolio"
                      className={`underline transition-colors duration-base ${
                        isDark
                          ? "text-blue-400 hover:text-blue-300"
                          : "text-blue-600 hover:text-blue-800"
                      }`}
                    >
                      Portfolio
                    </Link>
                  </li>
                  <li className={isDark ? "text-gray-600" : "text-text-light"}>
                    /
                  </li>
                  <li className={isDark ? "text-white" : "text-text"}>
                    Backend
                  </li>
                </ol>
              </nav>

              <h1
                className={`text-4xl font-display font-semibold mb-4 tracking-tight ${
                  isDark ? "text-white" : "text-text"
                }`}
              >
                Backend Projects
              </h1>
              <p
                style={{
                  fontSize: "1rem",
                  lineHeight: "1.75",
                  fontWeight: "400",
                  color: isDark
                    ? "oklch(55% 0.013 144.91)"
                    : "oklch(35% 0.013 144.91)",
                }}
              >
                My works on backend.
              </p>
            </header>

            {/* Projects List */}
            <div className="space-y-12">
              {projects.map((project, index) => (
                <article
                  key={index}
                  className={`pb-12 border-b last:border-b-0 ${
                    isDark ? "border-border-dark" : "border-border"
                  }`}
                >
                  {/* Project Title */}
                  <h2
                    className={`text-2xl font-display font-semibold mb-4 tracking-tight ${
                      isDark ? "text-white" : "text-text"
                    }`}
                  >
                    {project.title}
                  </h2>

                  {/* Project Description */}
                  <p
                    className="mb-6"
                    style={{
                      fontSize: "1rem",
                      lineHeight: "1.75",
                      fontWeight: "400",
                      color: isDark
                        ? "oklch(55% 0.013 144.91)"
                        : "oklch(35% 0.013 144.91)",
                    }}
                  >
                    {project.description}
                  </p>

                  {/* Areas of Focus */}
                  <div className="mb-6">
                    <h3
                      className={`text-lg font-display font-semibold mb-3 ${
                        isDark ? "text-white" : "text-text"
                      }`}
                    >
                      Areas of focus
                    </h3>
                    <ul
                      className="list-disc pl-6 space-y-2"
                      style={{
                        fontSize: "1rem",
                        lineHeight: "1.75",
                        fontWeight: "400",
                        color: isDark
                          ? "oklch(55% 0.013 144.91)"
                          : "oklch(35% 0.013 144.91)",
                      }}
                    >
                      {project.areasOfFocus.map((area, areaIndex) => (
                        <li key={areaIndex}>{area}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Tech Stack */}
                  <div className="mb-6">
                    <h3
                      className={`text-sm font-display font-semibold mb-3 uppercase tracking-wider ${
                        isDark ? "text-white" : "text-text"
                      }`}
                    >
                      Tech Stack
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {project.techStack.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className={`px-3 py-1.5 text-xs font-medium rounded ${
                            isDark
                              ? "bg-border-dark text-gray-300"
                              : "bg-secondary/20 text-text-muted"
                          }`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Project Links */}
                  <div className="flex items-center gap-4">
                    {project.githubUrl && (
                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 text-sm transition-colors duration-base ${
                          isDark
                            ? "text-gray-400 hover:text-primary-dark"
                            : "text-text-muted hover:text-primary"
                        }`}
                      >
                        <Github size={16} />
                        <span>View on GitHub</span>
                      </a>
                    )}
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 text-sm transition-colors duration-base ${
                          isDark
                            ? "text-gray-400 hover:text-primary-dark"
                            : "text-text-muted hover:text-primary"
                        }`}
                      >
                        <ExternalLink size={16} />
                        <span>Live Demo</span>
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
